import { useState,useEffect} from '@wordpress/element';
import {
    BorderBoxControl,
    __experimentalToolsPanel as ToolsPanel,
    __experimentalToolsPanelItem as ToolsPanelItem,
} from '@wordpress/components';

import { __experimentalBorderRadiusControl as BorderRadiusControl } from '@wordpress/block-editor';

import { __ } from '@wordpress/i18n';
import { VayuBlocksDevice,VayuBlocksDeviceControl } from '@vayu-blocks/device-control';
import { VayuBlocksBlockEditor } from '@vayu-blocks/global';
import {VayuBlocksDropShadow} from '@vayu-blocks/dropshadow';

/**
 * Vayu_Block_Border_Control Component
 * 
 * This component renders a control panel for managing border and border-radius settings.
 * It allows users to configure the border properties (color, style, width) and border-radius 
 * for each side of an element.
 * 
 * Props:
 * 
 * @param {string} [label='Border'] - The label for the border control panel. Defaults to 'Border'.
 * @param {object} [value] - The current border and border-radius settings. 
 *                            Should include `border` and `borderRadius` properties.
 * @param {string} [para='Configure border and border radius settings.'] - Description text for the control panel.
 * @param {function} onChange - Callback function that is called when border or border-radius values change.
 * @param {boolean} [includeBorder=true] - Whether to include the border controls in the panel.
 * @param {boolean} [includeBorderRadius=true] - Whether to include the border-radius controls in the panel.
 * @param {string} [borderLabel='Border'] - Label for the border control.
 * @param {string} [borderRadiusLabel='RADIUS'] - Label for the border-radius control.
 * @param {string} [color] - Default color for the border.
 * @param {boolean} [disableCustomColors=false] - Whether to disable custom color options.
 * @param {string} [popoverPlacement] - The placement of the color popover.
 * @param {boolean} [enableAlpha=true] - Whether to enable alpha transparency in color picker.
 * @param {boolean} [enableStyle=true] - Whether to enable style options.
 * 
 */

export function VayuBlocksBorder({
    onChange,
    label = {border:'Border',borderRadius:'Radius',dropShadow:'Drop Shadow'},
    border,
    borderRadius,
    dropShadow
}) {

    const deviceView  = VayuBlocksDevice();
    const blockEditor = VayuBlocksBlockEditor();
    const [colors, setColors] = useState([]);
    const [firstTablet, setFirstTablet] = useState(false);
    const [firstDesktop, setFirstDesktop] = useState(false);

    const handleBorderChangeTest = (type, newBorders) => {
        const key = type.key;
        const attr = type.value || {};
        const isFirstRun = attr?.[deviceView] === undefined;
        const isTabletFirstRun = localStorage.getItem('firstTablet') || firstTablet;
    
        const updatedAttributes = {
            ...attr,
            [deviceView]: newBorders
        };
    
        if (deviceView === 'Desktop') {
            if (isFirstRun || firstDesktop) {
                Object.assign(updatedAttributes, { Tablet: newBorders, Mobile: newBorders });
                localStorage.setItem('firstTablet', true);
                setFirstTablet(true);
                setFirstDesktop(true);
            }
        } else if (deviceView === 'Tablet') {
            if (isTabletFirstRun) {
                Object.assign(updatedAttributes, { Mobile: newBorders });
                localStorage.removeItem('firstTablet');
                setFirstTablet(true);
                setFirstDesktop(false);
            }
        } else {
            setFirstTablet(false);
            setFirstDesktop(false);
        }
    
        //setAttributes({ [key]: updatedAttributes });
        onChange(key, updatedAttributes);
    };

    const handleBorderRadiusNormalizedChange = (type, newValue) => {
        let normalizedValue = newValue;
    
        if (typeof newValue === 'string') {
            normalizedValue = {
                topLeft: newValue,
                topRight: newValue,
                bottomLeft: newValue,
                bottomRight: newValue
            };
        } else if (typeof newValue === 'object') {
            normalizedValue = {
                topLeft: newValue.topLeft,
                topRight: newValue.topRight,
                bottomLeft: newValue.bottomLeft,
                bottomRight: newValue.bottomRight
            };
        }
    
        handleBorderChangeTest(type, normalizedValue);
    };
    
    // default border colors
    useEffect(() => {
    setColors([
    {
        colors: blockEditor.colors,
        name: 'THEME COLORS'
      },
    {
        colors:[
        {
          color: '#72aee6',
          name: 'Blue 20'
        },
        {
          color: '#3582c4',
          name: 'Blue 40'
        },
        {
          color: '#e65054',
          name: 'Red 40'
        },
        {
          color: '#8a2424',
          name: 'Red 70'
        },
        {
          color: '#f2d675',
          name: 'Yellow 10'
        },
        {
          color: '#bd8600',
          name: 'Yellow 40'
        }],
        name: 'Default'
    }
      ]);
    }, [blockEditor]);

    const resetAll = () => {
       // const newAttributes = {};
        
        if (border?.key) {
            onChange(border.key, undefined);
           // newAttributes[border.key] = undefined;
        }
        
        if (borderRadius?.key) {
            onChange(borderRadius.key, undefined);
            //newAttributes[borderRadius.key] = undefined;
        }

        if (dropShadow?.key) {
            onChange(dropShadow.key, undefined);
        }
      
   };
    
    return (
        <div className="vayu_blocks_border_panel" >
            <ToolsPanel label={VayuBlocksDeviceControl({label:label.border, classname:'bold'})}
            resetAll={ resetAll }>
                   { border && <ToolsPanelItem
                     hasValue={ () => !! border?.value }
                        label={__(label.border, 'vayu_blocks')}
                        isShownByDefault
                        onDeselect={()=>onChange(border.key, undefined)}
                    >
                        <div class="vayu_block_border_control">
                        <BorderBoxControl
                            __next40pxDefaultSize={true}
                            enableAlpha
                            label={__(label.border, 'vayu_blocks')}
                            onChange={(newValue) => handleBorderChangeTest(border, newValue)}
                            value={border?.value?.[deviceView] ?? undefined}
                            colors={colors}
                        />
                        </div>
                    </ToolsPanelItem>}

                    {borderRadius && <ToolsPanelItem
                        hasValue={() => !! borderRadius?.value}
                        label={__(label.borderRadius, 'vayu_blocks')}
                        isShownByDefault
                        onDeselect={()=>onChange(borderRadius.key,undefined)}
                    >

                    <BorderRadiusControl
                        values={borderRadius?.value?.[deviceView] ?? undefined}
                        onChange={(newValue) => handleBorderRadiusNormalizedChange(borderRadius, newValue)}
                    />
                        
                    </ToolsPanelItem>}
                    {dropShadow && 
                    <ToolsPanelItem
                        hasValue={() => !! dropShadow?.value}
                        label={__('Box Shadow', 'vayu_blocks')}
                        isShownByDefault
                        onDeselect={()=>onChange(dropShadow.key,undefined)}
                    >
                       <VayuBlocksDropShadow 
                       onChange={onChange} 
                       label ={label.dropShadow} 
                       dropShadow = {dropShadow}
                       />
                    </ToolsPanelItem>
                    }
                    
            </ToolsPanel>
        </div>
    );
}
